import { useEffect, useRef, useState } from "react";
import * as echarts from "echarts/core";
import { LineChart } from "echarts/charts";
import { GridComponent, TooltipComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import type { DigestReport, Filters } from "../lib/api.ts";
import { chartTheme } from "../lib/chart.ts";
import { useThemeTick } from "./chrome.tsx";
import { useDrillOpener, type Drill } from "./dealList.tsx";
import { DrillNum, VALUE_BUTTON } from "./widgets.tsx";
import { daysAgoISO, money, num, pct, shortDate, todayISO, weekdayDate } from "../lib/format.ts";
import { currentLang, plural, t, type L } from "../i18n/lang.ts";
import { UI } from "../i18n/dict.ts";

echarts.use([LineChart, GridComponent, TooltipComponent, CanvasRenderer]);

/** Проза этого экрана. Один блок в начале файла — см. App.tsx. */
const TX = {
  /* --- шапка */
  against: { ru: "в сравнении с", en: "against" },
  sameWeekdayNotYesterday: {
    ru: ", тот же день недели неделю назад, а не вчера",
    en: ", the same weekday a week ago, not yesterday",
  },
  answeredIn: { ru: "ответ за", en: "answered in" },

  /* --- плитки */
  leadsYesterday: { ru: "Всего обращений за вчера", en: "Total inquiries yesterday" },
  qualifiedCounted: {
    one: { ru: "квалифицированный лид", en: "qualified lead" },
    few: { ru: "квалифицированных лида", en: "qualified leads" },
    many: { ru: "квалифицированных лидов", en: "qualified leads" },
  },
  sameWeekdayLastWeek: {
    ru: "в тот же день недели неделю назад",
    en: "the same weekday last week",
  },
  newDeals: { ru: "Новые сделки", en: "New deals" },
  nothingCreated: {
    ru: "в этот день сделок не появилось",
    en: "no deals appeared on this day",
  },
  withoutLeadCounted: {
    one: { ru: "из них без лида", en: "of them has no lead" },
    few: { ru: "из них без лида", en: "of them have no lead" },
    many: { ru: "из них без лида", en: "of them have no lead" },
  },
  allHaveLead: { ru: "у всех есть лид", en: "every one has a lead" },
  drillOpenList: {
    ru: "Показать записи, из которых сложилось это число",
    en: "Show the records this number is made of",
  },
  eventsClosed: { ru: "Завершённые праздники", en: "Completed events" },
  nothingClosed: {
    ru: "в этот день праздников не завершилось",
    en: "no events completed on this day",
  },
  ofThemNeverEntered: {
    one: { ru: "из них ни разу не проходил воронку", en: "of them never entered the pipeline" },
    few: { ru: "из них ни разу не проходили воронку", en: "of them never entered the pipeline" },
    many: { ru: "из них ни разу не проходили воронку", en: "of them never entered the pipeline" },
  },
  allCameThroughPipeline: {
    ru: "все они прошли через воронку",
    en: "all of them came through the pipeline",
  },
  /* Подпись плитки — общий термин `UI.depositConfirmed`: то же число под тем же
     именем стоит в недельном отчёте, а «Депозиты на счету» стояло здесь до
     10.09.2026 и не называло источник. */
  paymentsCounted: {
    one: { ru: "платёж", en: "payment" },
    few: { ru: "платежа", en: "payments" },
    many: { ru: "платежей", en: "payments" },
  },
  /* Подпись называет КОГОРТУ, а не повторяет слово «подтверждено»: деньги
     приходят по ссылке через день-два после заявки, поэтому платёж этого дня
     сплошь и рядом закрывает сделку прошлого. Пока это не написано, соседние
     плитки «Новые сделки» и «Платежи депозита» читаются как одно и то же. */
  noPaymentsThatDay: { ru: "платежей в этот день не было", en: "no payments that day" },
  forOlderDealCounted: {
    one: { ru: "по сделке прошлых дней", en: "for a deal from an earlier day" },
    few: { ru: "по сделкам прошлых дней", en: "for deals from earlier days" },
    many: { ru: "по сделкам прошлых дней", en: "for deals from earlier days" },
  },
  withoutDealCounted: {
    one: { ru: "без сделки", en: "with no deal" },
    few: { ru: "без сделки", en: "with no deal" },
    many: { ru: "без сделки", en: "with no deal" },
  },
  allForThatDay: { ru: "все по сделкам этого дня", en: "all for deals created that day" },
  tillRevenue: { ru: "Касса с банкетов", en: "Banquet POS revenue" },
  /* С 04.10.2026 — чеки праздников целиком (миграция 090): всё, что гости
     заплатили на празднике, включая еду и развлечения на нём. */
  tillIsBanquetOnly: {
    ru: "чеки праздников целиком, без билетов и еды в зале",
    en: "whole party checks, no tickets or walk-in food",
  },
  drillPartyChecks: { ru: "Чеки праздников за", en: "Party checks for" },
  drillPartyChecksHint: {
    ru: "банкетные заказы и заказы с пакетом, учётный день Syrve",
    en: "banquet orders and orders with a package, Syrve business day",
  },
  allLower: { ru: "все", en: "all" },
  parksReportTill: {
    one: { ru: "парка отдаёт кассу", en: "parks report a till" },
    few: { ru: "парков отдают кассу", en: "parks report a till" },
    many: { ru: "парков отдают кассу", en: "parks report a till" },
  },
  theOther: { ru: "у остальных", en: "the other" },
  noTillAccess: {
    ru: "нет доступа к кассе, поэтому цифра занижена — это не парк без продаж",
    en: "have no till access, so this is understated, not a park without sales",
  },

  /* --- график */
  /* Заголовок графика назван обращениями, как и его собственная легенда двумя
     строками выше: линия считает ВСЕ созданные записи, а не отобранный поток. */
  leadsAndHandoffs: {
    ru: "Обращения и квалифицированные лиды, последние 45 дней",
    en: "Inquiries and qualified leads, last 45 days",
  },
  leadsAndHandoffsHint: {
    ru: "созданы в CRM, и сколько из них стали квалифицированными лидами",
    en: "created in the CRM, and how many of them became qualified leads",
  },
  leadsCreated: { ru: "Созданные обращения", en: "Inquiries created" },
  qualifiedLeads: { ru: "Квалифицированные лиды", en: "Qualified leads" },

  /* --- на что взглянуть */
  worthALookTitle: { ru: "На что взглянуть", en: "Worth a look" },
  drillConversations: { ru: "Обращения за", en: "Inquiries on" },
  drillConversationsHint: {
    ru: "созданы за эти сутки по времени парка",
    en: "created during this day, park time",
  },
  drillQualified: { ru: "Квалифицированные лиды", en: "Qualified leads" },
  drillNoLead: { ru: "Сделки без лида", en: "Deals with no lead" },
  drillEvents: { ru: "Праздники", en: "Events on" },
  drillEventsHint: {
    ru: "по дате праздника, депозит внесён, сделка не проиграна",
    en: "by event date, deposit taken, deal not lost",
  },
  drillPayments: { ru: "Сделки с платежом за", en: "Deals paid on" },
  drillPaymentsHint: {
    ru: "список по сделкам — платежи без сделки в него не попадают",
    en: "the list is of deals — payments with no deal are not in it",
  },
  drillNoReply: { ru: "Никто не ответил", en: "Nobody replied" },
  drillNoReplyHint: {
    ru: "последние семь дней, ни бот, ни сотрудник",
    en: "the last seven days, neither the bot nor a person",
  },
  eventsAllClosed: { ru: "все закрыты в CRM", en: "all closed in the CRM" },
  eventsClosedOf: { ru: "закрыто в CRM", en: "closed in the CRM" },
  nothingHappened: {
    ru: "в этот день праздников не было",
    en: "no events took place on this day",
  },
  openFullList: { ru: "Раскрыть полный список", en: "Open the full list" },
  kindNoReply: { ru: "Никто не ответил", en: "Nobody replied" },
  conversationsCounted: {
    one: { ru: "обращение", en: "inquiry" },
    few: { ru: "обращения", en: "inquiries" },
    many: { ru: "обращений", en: "inquiries" },
  },
  noReplyBody: {
    ru: "за последнюю неделю: клиент написал, и ему не ответил никто — ни бот, ни человек; лиды и сделки, закрытые как проигранные, не считаются. Считается по диалогам открытых линий: у лида, заведённого звонком или руками, переписки нет.",
    en: "in the last week: the client wrote and nobody replied — neither the bot nor a person; leads and deals closed as lost are left out. Counted over open-line chats: a lead created by a call or by hand has no chat to measure.",
  },
  andMore: { ru: "ещё", en: "and" },
  nothingNeedsALook: {
    ru: "Сегодня утром смотреть не на что. Возраст воронки, разнесение расходов, метки источников и свежесть источников — всё чисто.",
    en: "Nothing needs a look this morning. Pipeline age, ad spend attribution, source tags and feed freshness are all clean.",
  },
  kindSourceBehind: { ru: "Источник отстал", en: "Source behind" },
  feedsCounted: {
    one: { ru: "источник", en: "feed" },
    few: { ru: "источника", en: "feeds" },
    many: { ru: "источников", en: "feeds" },
  },
  /* Местоимение и согласование глагола зависят от числа, поэтому вся фраза
     идёт формами, а не склеивается из куска и суффикса. */
  feedsBehindBody: {
    one: {
      ru: "не завершил прогон в срок. Каждая цифра, построенная на нём, занижена, а не равна нулю.",
      en: "have not finished a run on time. Every number built on it is understated, not zero.",
    },
    few: {
      ru: "не завершили прогон в срок. Каждая цифра, построенная на них, занижена, а не равна нулю.",
      en: "have not finished a run on time. Every number built on them is understated, not zero.",
    },
    many: {
      ru: "не завершили прогон в срок. Каждая цифра, построенная на них, занижена, а не равна нулю.",
      en: "have not finished a run on time. Every number built on them is understated, not zero.",
    },
  },
  neverFinished: { ru: "ни разу не завершался", en: "never finished" },
  hoursAgo: { ru: "ч назад", en: "h ago" },
  kindAgeingPipeline: { ru: "Воронка стареет", en: "Ageing pipeline" },
  leadsCounted: {
    one: { ru: "лид", en: "lead" },
    few: { ru: "лида", en: "leads" },
    many: { ru: "лидов", en: "leads" },
  },
  ageingBody: {
    one: {
      ru: "лежит в воронке больше 30 дней",
      en: "has sat in the pipeline for more than 30 days",
    },
    few: {
      ru: "лежат в воронке больше 30 дней",
      en: "have sat in the pipeline for more than 30 days",
    },
    many: {
      ru: "лежат в воронке больше 30 дней",
      en: "have sat in the pipeline for more than 30 days",
    },
  },
  oldestIs: { ru: ". Самому старому", en: ". The oldest is" },
  daysOldCounted: {
    one: { ru: "день", en: "days old" },
    few: { ru: "дня", en: "days old" },
    many: { ru: "дней", en: "days old" },
  },
  eachWorthACall: {
    ru: ". Каждый стоит либо звонка, либо закрытия.",
    en: ". Each one is worth either a call or a close.",
  },
  kindSpendWithoutPark: { ru: "Расход без парка", en: "Spend without a park" },
  spendNoParkRule: {
    ru: "расхода за последние 30 дней не подошли ни под одно правило парка",
    en: "of the last 30 days of ad spend matched no park rule",
  },
  acrossDays: { ru: ", за", en: ", across" },
  daysCounted: {
    one: { ru: "день", en: "days" },
    few: { ru: "дня", en: "days" },
    many: { ru: "дней", en: "days" },
  },
  spendCountsInTotal: {
    ru: ". Он входит в итог и ни в одну строку по паркам.",
    en: ". It counts in the total and in none of the per-park rows.",
  },
  kindTagsWithoutRule: { ru: "Метки без правила", en: "Tags without a rule" },
  carryOfLast30: { ru: "за последние 30 дней несут", en: "of the last 30 days carry" },
  sourceTagsCounted: {
    one: { ru: "метку источника", en: "source tag" },
    few: { ru: "метки источника", en: "source tags" },
    many: { ru: "меток источника", en: "source tags" },
  },
  noRuleParses: {
    ru: "— их не разбирает ни одно правило. Канал таких лидов не определён, а не органический.",
    en: "that no rule parses. Their channel is unknown, not organic.",
  },

  /* --- списки под карточками «на что взглянуть» */
  drillAgeingLeads: {
    ru: "Лиды, лежащие в воронке больше 30 дней",
    en: "Leads sitting in the pipeline for more than 30 days",
  },
  drillAgeingHint: {
    ru: "стоят на стадии передачи менеджеру ОП прямо сейчас; период сверху к этому отбору не применяется",
    en: "currently standing on a handoff stage; the period filter above does not apply to this cohort",
  },
  drillUnmappedLeads: {
    ru: "Лиды с меткой, для которой нет правила",
    en: "Leads whose source tag no rule parses",
  },
  drillUnmappedHint: {
    ru: "последние 30 дней по дате создания лида — ровно так считает карточка",
    en: "the last 30 days by lead creation date — exactly as the card counts",
  },

  /* --- что предстоит */
  comingUpInParks: { ru: "Что предстоит в парках", en: "Coming up in the parks" },
  comingUpWindow: { ru: "праздники на 7 дней, считая сегодня", en: "events in the next 7 days, today included" },
  noEventsSevenDays: {
    ru: "на 7 дней, считая сегодня, праздников не забронировано",
    en: "no events booked for the next 7 days, today included",
  },
  withoutPaidBooking: { ru: "без оплаченной брони", en: "without a paid booking" },
  allHavePaidBooking: { ru: "у всех есть оплаченная бронь", en: "all have a paid booking" },
  drillComingUp: { ru: "Праздники ближайших 7 дней", en: "Events in the next 7 days" },
  /* «По дате праздника» панель пишет сама — здесь только то, чего она не знает. */
  drillComingUpHint: {
    ru: "сделки не проигранные · период сверху не применяется",
    en: "deals not lost · the period above does not apply",
  },
} satisfies Record<string, L | { one: L; few: L; many: L }>;

/**
 * Утренний дайджест.
 *
 * Экран собран по дизайн-канвасу и ничего сам не считает: все цифры приходят
 * готовыми из `api.digest()`, где каждая определена тем же выражением, что на
 * своём экране. Здесь только раскладка и подписи.
 *
 * Сравнение идёт с ТЕМ ЖЕ ДНЁМ НЕДЕЛИ неделю назад, а не со вчера, — это
 * названо в подписи над плитками. Без этого читатель по привычке решит, что
 * сравнивают со вчера, и увидит в −6 % падение, которого нет.
 */

type Delta = { tone: "up" | "down" | "flat"; text: string };

function toneOf(value: number, prev: number): Delta["tone"] {
  return value > prev ? "up" : value < prev ? "down" : "flat";
}

/**
 * Изменение в процентах. Прошлое значение ноль — показываем «—»: процент от
 * нуля это не рост, а деление на ноль, и в плитке ему делать нечего.
 */
function pctDelta(value: number, prev: number): Delta {
  const tone = toneOf(value, prev);
  if (prev === 0) return { tone, text: "—" };
  const text = pct(value - prev, prev, 0);
  return { tone, text: value > prev ? `+${text}` : text };
}

/**
 * Изменение в штуках. У суточных счётчиков в единицах процент бессмыслен: одно
 * состоявшееся событие против одного даёт «0 %», а одно против нуля — «+100 %»,
 * хотя случилось ровно одно событие. Разница в штуках говорит то, что есть.
 */
function countDelta(value: number, prev: number): Delta {
  const d = value - prev;
  const sign = d > 0 ? "+" : d < 0 ? "−" : "±";
  return { tone: toneOf(value, prev), text: `${sign}${num(Math.abs(d))}` };
}

/**
 * Искра рядом с числом: простой polyline, не echarts. Рядом с цифрой нужна
 * форма ряда, а не график — ни осей, ни подсказок, ни четырёх лишних canvas.
 *
 * Масштаб — по собственному размаху ряда, а не от нуля: уровень уже сказан
 * числом слева и значком изменения, искра отвечает только за форму. От нуля
 * лиды (сотня с небольшим каждый день) вырождаются в прямую у верхнего края.
 */
function Spark({ values, color }: { values: number[]; color: string }): React.ReactElement {
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min;
  const step = values.length > 1 ? 100 / (values.length - 1) : 0;
  /* Ровный ряд (и ряд из одних нулей) кладём по середине: делить на нулевой
     размах нельзя, а линия по верхнему краю читалась бы как рост. */
  const points = values
    .map((v, i) => {
      const y = span > 0 ? 23 - (20 * (v - min)) / span : 13;
      return `${(i * step).toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg
      viewBox="0 0 100 26"
      preserveAspectRatio="none"
      style={{ width: "100%", height: 26, marginTop: 11, overflow: "visible" }}
      aria-hidden="true"
    >
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function Tile({
  tint,
  label,
  value,
  unit,
  delta,
  spark,
  foot,
  drill,
}: {
  /** Цвет метки и искры одной плитки. Переменная темы, не hex: переключение
   *  светлой темы не должно оставлять плитку с чужим цветом. */
  tint: string;
  label: string;
  value: string;
  unit?: string;
  /* Сравнение и искра необязательны: у плитки «Что предстоит» нет ни
     вчерашнего значения, ни ряда — это расписание вперёд, а не итог дня. */
  delta?: Delta;
  spark?: number[];
  /* Разметка, а не строка: в подвале живут кликабельные числа — «дошли до
     менеджера ОП», «без лида». Число, которое нельзя раскрыть, проверяется
     только руками в Битриксе. */
  foot: React.ReactNode;
  /**
   * Детализация под цифрой. Необязательна: у остальных плиток экрана списка
   * нет, потому что их срезы считаются по дубайским суткам, а список сделок
   * умеет только те базисы даты, которые ему объявлены (`create_local`).
   * Плитка без `drill` остаётся ровно такой, какой была.
   */
  drill?: Drill;
}): React.ReactElement {
  /* Хук зовётся всегда, до любых ветвлений: порядок вызова хуков не должен
     зависеть от того, есть у плитки детализация или нет. */
  const openDrill = useDrillOpener();
  return (
    /* Цвет метки задаётся переменной, а не вложенным элементом: тем же
       приёмом плитки различаются на всех остальных экранах, и рисовать метку
       здесь во второй раз значило бы держать два механизма для одного. */
    <div className="card kpi" style={{ "--kpi-tint": tint } as React.CSSProperties}>
      <span className="label">{label}</span>
      {drill !== undefined && openDrill !== null ? (
        <button
          type="button"
          style={VALUE_BUTTON}
          title={t(TX.drillOpenList)}
          onClick={() => openDrill(drill)}
        >
          <span className="value">
            {value}
            {unit && <span className="unit">{unit}</span>}
          </span>
        </button>
      ) : (
        <span className="value">
          {value}
          {unit && <span className="unit">{unit}</span>}
        </span>
      )}{" "}
      {delta !== undefined && <span className={`delta ${delta.tone}`}>{delta.text}</span>}
      {spark !== undefined && <Spark values={spark} color={tint} />}
      <span className="foot">{foot}</span>
    </div>
  );
}

function TrendChart({ trend }: { trend: DigestReport["trend"] }): React.ReactElement {
  const ref = useRef<HTMLDivElement | null>(null);
  const tick = useThemeTick();
  /* Подписи рядов переводим ДО входа в эффект: внутри `t` — это тема графика
     (`chartTheme()`), а не перевод. Смену языка ловит тот же `tick`, что и смену
     темы, поэтому график пересобирается сам. */
  const seriesLeads = t(TX.leadsCreated);
  const seriesQualified = t(TX.qualifiedLeads);
  useEffect(() => {
    if (!ref.current) return;
    /* Цвета читаем здесь, а не снаружи: нужны те значения, что стоят в теме на
       момент отрисовки. `tick` в зависимостях перерисовывает график после
       переключения темы — сам echarts о смене оформления не узнаёт. */
    const t = chartTheme();
    const chart = echarts.init(ref.current, undefined, { renderer: "canvas" });
    chart.setOption({
      grid: { left: 8, right: 8, top: 18, bottom: 4, containLabel: true },
      tooltip: { trigger: "axis", ...t.tooltip },
      xAxis: { type: "category", data: trend.map((r) => shortDate(r.d)), ...t.axis },
      yAxis: { type: "value", ...t.axis, splitLine: t.splitLine },
      series: [
        {
          name: seriesLeads,
          type: "line",
          smooth: true,
          symbol: "none",
          data: trend.map((r) => r.leads),
          lineStyle: { width: 2, color: t.accent },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: `${t.accent}55` },
              { offset: 1, color: `${t.accent}00` },
            ]),
          },
        },
        {
          name: seriesQualified,
          type: "line",
          smooth: true,
          symbol: "none",
          data: trend.map((r) => r.qualified),
          lineStyle: { width: 2, color: t.pos },
        },
      ],
    });
    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(ref.current);
    return () => {
      ro.disconnect();
      chart.dispose();
    };
  }, [trend, tick]);
  return <div ref={ref} style={{ height: 268 }} />;
}

/**
 * Раскрывающееся число в карточке «на что взглянуть».
 *
 * Две карточки из четырёх уже проваливались в список лидов — там объект
 * детализации лид, и для него есть готовая панель. У двух других объект другой:
 * прогон ETL и рекламная кампания. Заводить ради них полноценные панели и ручки
 * значит построить две трети экрана администрирования заново; при этом весь
 * список целиком — это меньше двадцати строк у источников и сотня у кампаний.
 * Поэтому «проваливание» здесь — раскрытие полного списка на месте.
 *
 * Свёрнутым показывается ПЕРВЫЙ десяток: карточка не должна занимать экран,
 * пока её не попросили.
 */
function Expandable({
  value,
  label,
  items,
  collapsed = 10,
}: {
  value: React.ReactNode;
  label: string;
  items: React.ReactNode[];
  collapsed?: number;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const shown = open ? items : items.slice(0, collapsed);
  const hidden = items.length - shown.length;
  return (
    <>
      <button
        type="button"
        className="link-num"
        aria-expanded={open}
        title={label}
        onClick={() => setOpen(!open)}
      >
        {value}
      </button>
      {items.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {shown}
          {hidden > 0 && (
            <button type="button" className="tag tag-more" onClick={() => setOpen(true)}>
              {t(TX.andMore)} {num(hidden)}
            </button>
          )}
        </div>
      )}
    </>
  );
}

type Look = {
  key: string;
  kind: string;
  /** Насколько громко. `neg` — цифра на экранах уже неверна, `warn` — работа
   *  копится, `quiet` — дырка в справочниках, лечится одной строкой. */
  tone: "neg" | "warn" | "quiet";
  body: React.ReactNode;
};

function LookCard({ look }: { look: Look }): React.ReactElement {
  const framed = look.tone !== "quiet";
  return (
    <div
      className={framed ? "note" : "card"}
      style={
        look.tone === "neg"
          ? { background: "var(--neg-soft)", borderColor: "var(--neg)" }
          : undefined
      }
    >
      <span
        className={look.tone === "warn" ? "tag tag-warn" : "tag"}
        style={
          look.tone === "neg" ? { background: "var(--card)", color: "var(--neg)" } : undefined
        }
      >
        {look.kind}
      </span>
      <div style={{ marginTop: 7, fontSize: 13.5, color: "var(--ink)", lineHeight: 1.5 }}>
        {look.body}
      </div>
    </div>
  );
}

/**
 * «Worth a look».
 *
 * Пункт показывается ТОЛЬКО когда за ним стоит число. Спокойная карточка
 * «здесь всё хорошо» на каждый повод учит пролистывать список целиком, и тогда
 * настоящий повод пролистают вместе с остальными.
 */
function worthALook(a: DigestReport["attention"], day: string): Look[] {
  const looks: Look[] = [];

  const stale = a.staleSources;
  if (stale.length > 0) {
    /* Число берётся с сервера, а не из длины массива: строк приезжает не больше
       восьми, и «8 источников» при шестнадцати отставших — цифра, которая
       врёт ровно тогда, когда беда крупнее. Замер 13.09.2026: 16 против 8. */
    const staleTotal = stale[0]?.stale_total ?? stale.length;
    looks.push({
      key: "stale",
      kind: t(TX.kindSourceBehind),
      tone: "neg",
      body: (
        <>
          <Expandable
            label={t(TX.openFullList)}
            value={
              <b>
                {num(staleTotal)} {plural(staleTotal, TX.feedsCounted)}
              </b>
            }
            items={stale.map((s) => (
              <span className="tag" key={s.name}>
                {s.name} ·{" "}
                {s.hours_ago === null
                  ? t(TX.neverFinished)
                  : `${num(s.hours_ago)} ${t(TX.hoursAgo)}`}
                {s.status !== "ok" ? ` · ${s.status}` : ""}
              </span>
            ))}
          />{" "}
          {plural(staleTotal, TX.feedsBehindBody)}
        </>
      ),
    });
  }

  /*
    * «Никто не ответил» стоит ПЕРВЫМ среди поводов, и это не про оформление.
    * Отставший источник — наша беда, стареющая воронка — работа на неделю, а
    * клиент, который написал и ждёт третьи сутки, уходит к конкуренту сегодня.
    */
  const waiting = a.noReply;
  if (waiting.length > 0) {
    const total = waiting[0]?.total ?? waiting.length;
    looks.push({
      key: "no-reply",
      kind: t(TX.kindNoReply),
      tone: "neg",
      body: (
        <>
          {/*
            ПАНЕЛЬ СБОКУ, А НЕ СПИСОК ВНУТРИ КАРТОЧКИ. Раньше здесь
            раскрывались все обращения плашками — на замере 18.09.2026 это 390
            строк одним полотном, поверх которого не видно ни остальных
            поводов, ни таблицы ближайших праздников.

            И главное: плашка была мёртвой. Обращение без ответа — повод пойти
            и ОТВЕТИТЬ, а для этого нужен лид или сделка, к которым диалог
            прикреплён. В панели у каждой строки есть переход в карточку.

            Окно своё, семидневное, как у самой карточки: диалог, оставшийся
            без ответа в субботу, в понедельник по-прежнему без ответа.
          */}
          <b>
            <DrillNum
              drill={{
                kind: "conversations",
                title: t(TX.drillNoReply),
                hint: t(TX.drillNoReplyHint),
                slice: { answered: false },
                filters: { ...dayWindow(day), from: daysAgoISO(6, day) },
              }}
            >
              {num(total)} {plural(total, TX.conversationsCounted)}
            </DrillNum>
          </b>{" "}
          {t(TX.noReplyBody)}
        </>
      ),
    });
  }

  if (a.ageing.leads > 0) {
    looks.push({
      key: "ageing",
      kind: t(TX.kindAgeingPipeline),
      tone: "warn",
      body: (
        <>
          <b>
            {/* Совокупность карточки от периода НЕ зависит: она про то, что
                лежит в воронке сейчас. Поэтому список открывается со своим
                окном — иначе под числом 6 оказалось бы меньше строк, а разницу
                прочитали бы как ошибку. Парки не трогаем: их карточка
                применяет.

                Сверка 13.09.2026 («6 и 6») была неверной: она вызывала функцию
                списка напрямую, мимо маршрута, а маршрут `/api/leads` срез
                `ageOrder` не разбирал вовсе. Список открывал ВСЕ лиды парка с
                2020 года — по Dalma 6 434 строки под числом 4, включая
                вчерашние. С 14.10.2026 маршрут разбирает срез одним полным
                разборщиком (`leadSliceFromQuery`), и сверка идёт через HTTP:
                все парки 261 и 261, Dalma 3 и 3, Zamania 244 и 244. */}
            <DrillNum
              drill={{
                title: t(TX.drillAgeingLeads),
                hint: t(TX.drillAgeingHint),
                slice: { ageOrder: 4 },
                filters: {
                  from: "2020-01-01",
                  to: todayISO(),
                  era: "all",
                  brand: undefined,
                  channel: [],
                },
              }}
            >
              {num(a.ageing.leads)} {plural(a.ageing.leads, TX.leadsCounted)}
            </DrillNum>
          </b>{" "}
          {plural(a.ageing.leads, TX.ageingBody)}
          {a.ageing.oldest_days > 0
            ? `${t(TX.oldestIs)} ${num(a.ageing.oldest_days)} ${plural(
                a.ageing.oldest_days,
                TX.daysOldCounted,
              )}`
            : ""}
          {t(TX.eachWorthACall)}
        </>
      ),
    });
  }

  if (Number(a.unknownSpend.aed_minor) > 0) {
    looks.push({
      key: "spend",
      kind: t(TX.kindSpendWithoutPark),
      tone: "quiet",
      body: (
        <>
          {/* Сами кампании, а не только итог: у денег объект детализации —
              кампания, и отдельной панели ради сотни строк заводить незачем. */}
          <Expandable
            label={t(TX.openFullList)}
            value={<b>{money(a.unknownSpend.aed_minor, "AED")}</b>}
            items={a.unknownSpend.campaigns.map((c) => (
              <span className="tag" key={`${c.platform}/${c.account_name ?? ""}/${c.name ?? ""}`}>
                {c.name ?? "—"}
                {c.account_name ? ` · ${c.account_name}` : ""} ·{" "}
                {money(c.aed_minor, "AED", { withCode: false })}
              </span>
            ))}
          />{" "}
          {t(TX.spendNoParkRule)}
          {a.unknownSpend.days > 0
            ? `${t(TX.acrossDays)} ${num(a.unknownSpend.days)} ${plural(
                a.unknownSpend.days,
                TX.daysCounted,
              )}`
            : ""}
          {t(TX.spendCountsInTotal)}
        </>
      ),
    });
  }

  if (a.unmapped.leads > 0) {
    looks.push({
      key: "unmapped",
      kind: t(TX.kindTagsWithoutRule),
      tone: "quiet",
      body: (
        <>
          {/* Число согласовано с существительным: «1 leads» на экране у клиента
              читается как недоделка, и доверие к остальным цифрам падает. В
              русском форм три, поэтому и здесь `plural`, а не суффикс «s». */}
          <b>
            {/* Карточка считает свои 30 дней и НЕ применяет ни парки, ни эпоху:
                дырка в справочнике меток — вопрос общий, а не парковый. Список
                идёт тем же окном, иначе он разойдётся с числом над собой.
                Сверено 13.09.2026: 1 и 1. */}
            <DrillNum
              drill={{
                title: t(TX.drillUnmappedLeads),
                hint: t(TX.drillUnmappedHint),
                slice: { unmappedTag: true },
                filters: {
                  from: daysAgoISO(30),
                  to: todayISO(),
                  era: "all",
                  /* Парки НЕ снимаем: с 13.09.2026 карточка их применяет, и
                     список обязан отбирать ту же совокупность. Бренд и канал
                     снимаем — их карточка не применяет, а список применил бы. */
                  brand: undefined,
                  channel: [],
                },
              }}
            >
              {num(a.unmapped.leads)} {plural(a.unmapped.leads, TX.leadsCounted)}
            </DrillNum>
          </b>{" "}
          {t(TX.carryOfLast30)} {num(a.unmapped.tags)}{" "}
          {plural(a.unmapped.tags, TX.sourceTagsCounted)} {t(TX.noRuleParses)}
        </>
      ),
    });
  }

  return looks;
}

/**
 * «Sat, 12 Sep». День недели показан намеренно: у детского парка выходной —
 * другой день, чем вторник, и по одной дате это не видно. Дата собирается с
 * полуночи местного дня, иначе строка «2026-09-12» в поясе западнее UTC
 * съезжает на сутки назад.
 *
 * Локаль читается из языка интерфейса, как в `lib/format.ts`. Готовой функции с
 * днём недели там нет, поэтому она собирается здесь — но порядок «день месяц»
 * тот же, что у `shortDate()`, и en-GB, а не en-US: он принят и в ОАЭ, и в
 * Бахрейне, и совпадает с русским.
 */
/**
 * Дата с днём недели. Сам формат живёт в `lib/format.ts` рядом с остальными:
 * форматирование — одно место на всё приложение, иначе языки разъедутся по
 * файлам. Здесь остаётся только приведение «дата без времени» к локальной
 * полуночи, чтобы день не сползал на предыдущий.
 */
function dayLabel(iso: string): string {
  return weekdayDate(`${iso}T00:00:00`);
}

/**
 * Подпись плитки платежей: сколько их и к сделкам какого дня они относятся.
 *
 * Три ветви складываются в одно предложение, потому что вопрос заказчика был
 * ровно про стык двух плиток: «новая сделка одна, а платежей на 3 000». Пока
 * подпись говорила только «2 платежа · подтверждено системой», ответить на
 * этот вопрос экран не мог.
 *
 * Ветвь «без сделки» печатается, только когда такие платежи есть: по базе их
 * 12 из 165, и молчать о них нельзя, а показывать «0 без сделки» каждый день —
 * шум.
 */
/**
 * Окно списка под плиткой дайджеста: ровно отчётные сутки.
 *
 * Экран не применяет ни период с верхней панели, ни эпоху, ни бренд — значит и
 * списки под его числами применять их не должны, иначе под цифрой «4» откроются
 * две сделки. Парки НЕ снимаем: их дайджест применяет, и они приходят из
 * текущих фильтров сами.
 *
 * Один помощник на все плитки, а не пять одинаковых объектов: разойдись они
 * хоть одним полем, и два списка одного экрана начали бы отбирать разное.
 */
function dayWindow(day: string): Partial<Filters> {
  return { from: day, to: day, era: "all", brand: undefined, channel: [] };
}

function depositFoot(dep: DigestReport["deposits"]): string {
  if (dep.payments === 0) return t(TX.noPaymentsThatDay);
  const parts = [`${num(dep.payments)} ${plural(dep.payments, TX.paymentsCounted)}`];
  if (dep.for_older_deal > 0) {
    parts.push(`${num(dep.for_older_deal)} ${plural(dep.for_older_deal, TX.forOlderDealCounted)}`);
  }
  if (dep.without_deal > 0) {
    parts.push(`${num(dep.without_deal)} ${plural(dep.without_deal, TX.withoutDealCounted)}`);
  }
  if (parts.length === 1) parts.push(t(TX.allForThatDay));
  return parts.join(" · ");
}

export function DigestScreen({ report }: { report: DigestReport }): React.ReactElement {
  const spark = report.trend.slice(-report.sparkDays);
  const looks = worthALook(report.attention, report.day);
  const pos = report.pos;
  const parksSilent = pos.parks_total - pos.parks_reporting;


  return (
    <div className="grid">
      {/* Отчётный день теперь назван в заголовке экрана тем же кеглем, что и
          сам заголовок, — здесь остаётся только база сравнения. Печатать дату
          дважды подряд, крупно и мелко, значит вернуть ту же жалобу. */}
      <p className="sub" style={{ margin: 0 }}>
        {t(TX.against)} {dayLabel(report.prevDay)}
        {t(TX.sameWeekdayNotYesterday)} · {t(TX.answeredIn)} {num(report.queryMs)} ms
      </p>

      <div className="grid cols-5">
        {/*
          Когорта `created_local` — сутки по времени ПАРКА, как и сама плитка.
          Обычный `created` меряет UTC-сутками, и на потоке в двести с лишним
          обращений в день список разошёлся бы с числом над собой на каждой
          полуночи.
        */}
        <Tile
          tint="var(--s1)"
          label={t(TX.leadsYesterday)}
          value={num(report.leads.value)}
          delta={pctDelta(report.leads.value, report.leads.prev)}
          spark={spark.map((r) => r.leads)}
          drill={{
            title: `${t(TX.drillConversations)} ${dayLabel(report.day)}`,
            hint: t(TX.drillConversationsHint),
            slice: { cohort: "created_local" },
            filters: dayWindow(report.day),
          }}
          foot={
            <>
              <DrillNum
                drill={{
                  title: `${t(TX.drillQualified)} · ${dayLabel(report.day)}`,
                  hint: t(TX.drillConversationsHint),
                  slice: { cohort: "created_local", qualified: true },
                  filters: dayWindow(report.day),
                }}
              >
                {num(report.leads.qualified)}
              </DrillNum>{" "}
              {plural(report.leads.qualified, TX.qualifiedCounted)} ·{" "}
              {num(report.leads.prev)} {t(TX.sameWeekdayLastWeek)}
            </>
          }
        />
        <Tile
          tint="var(--s5)"
          label={t(TX.newDeals)}
          value={num(report.newDeals.value)}
          delta={countDelta(report.newDeals.value, report.newDeals.prev)}
          spark={spark.map((r) => r.deals)}
          foot={
            report.newDeals.value === 0 ? (
              t(TX.nothingCreated)
            ) : report.newDeals.no_lead > 0 ? (
              <>
                {/* Сделка без лида не имеет источника и не видна ни в одной
                    конверсии по лидам — её и нужно уметь открыть поимённо. */}
                <DrillNum
                  drill={{
                    kind: "deals",
                    title: `${t(TX.drillNoLead)} · ${dayLabel(report.day)}`,
                    slice: { dateBasis: "create_local", hasLead: false },
                    filters: dayWindow(report.day),
                  }}
                >
                  {num(report.newDeals.no_lead)}
                </DrillNum>{" "}
                {plural(report.newDeals.no_lead, TX.withoutLeadCounted)} ·{" "}
                {num(report.newDeals.prev)} {t(TX.sameWeekdayLastWeek)}
              </>
            ) : (
              `${t(TX.allHaveLead)} · ${num(report.newDeals.prev)} ${t(TX.sameWeekdayLastWeek)}`
            )
          }
          /* Окно задаётся плиткой, а не берётся с верхней панели: экран про
             сутки. Базис `create_local` считает те же дубайские сутки, что и
             сама плитка, — иначе «1» открывала бы список на 3 строки. */
          drill={{
            kind: "deals",
            title: `${t(TX.newDeals)} · ${dayLabel(report.day)}`,
            slice: { dateBasis: "create_local" },
            filters: {
              from: report.day,
              to: report.day,
              /* Дайджест не применяет ни эпоху, ни бренд — значит и список
                 не должен: иначе с выбранным брендом под «4» открылись бы
                 две сделки. Парки остаются: их дайджест применяет. */
              era: "all",
              brand: undefined,
            },
          }}
        />
        {/*
          Считается по дате ПРАЗДНИКА, а не по дате закрытия сделки, — и подпись
          под числом говорит, сколько из прошедших уже закрыто в CRM. Разрыв
          между двумя числами это и есть польза плитки: 17.09.2026 он равнялся
          «прошёл 1, закрыт 0», и увидеть это больше негде.
        */}
        <Tile
          tint="var(--s2)"
          label={t(TX.eventsClosed)}
          value={num(report.won.value)}
          delta={countDelta(report.won.value, report.won.prev)}
          spark={spark.map((r) => r.won)}
          drill={{
            kind: "deals",
            title: `${t(TX.drillEvents)} ${dayLabel(report.day)}`,
            hint: t(TX.drillEventsHint),
            /* Подтверждённый депозит — то же условие, что у плитки (миграция 097). */
            slice: { dateBasis: "event", outcome: "not_lost", depositConfirmed: true },
            filters: dayWindow(report.day),
          }}
          foot={
            report.won.value === 0
              ? t(TX.nothingHappened)
              : [
                  report.won.closed === report.won.value
                    ? t(TX.eventsAllClosed)
                    : `${t(TX.eventsClosedOf)} ${num(report.won.closed)} ${t(UI.of)} ${num(
                        report.won.value,
                      )}`,
                  report.won.no_pipeline > 0
                    ? `${num(report.won.no_pipeline)} ${plural(
                        report.won.no_pipeline,
                        TX.ofThemNeverEntered,
                      )}`
                    : t(TX.allCameThroughPipeline),
                ].join(" · ")
          }
        />
        <Tile
          tint="var(--s3)"
          label={t(UI.depositConfirmed)}
          value={money(report.deposits.value, "AED", { compact: true, withCode: false })}
          unit="AED"
          delta={pctDelta(Number(report.deposits.value), Number(report.deposits.prev))}
          spark={spark.map((r) => Number(r.deposit_aed_minor))}
          /*
           * Базис `paid` — дата, когда деньги реально пришли в систему
           * бронирования. Список короче числа платежей ровно на те, у которых
           * сделки нет вовсе; их количество печатает подпись под плиткой, а
           * подсказка списка называет причину.
           */
          drill={{
            kind: "deals",
            title: `${t(TX.drillPayments)} ${dayLabel(report.day)}`,
            hint: t(TX.drillPaymentsHint),
            slice: { dateBasis: "paid" },
            filters: dayWindow(report.day),
          }}
          foot={depositFoot(report.deposits)}
        />
        <Tile
          tint="var(--s4)"
          label={t(TX.tillRevenue)}
          value={money(pos.value, "AED", { compact: true, withCode: false })}
          unit="AED"
          delta={pctDelta(Number(pos.value), Number(pos.prev))}
          spark={spark.map((r) => Number(r.pos_aed_minor))}
          /*
           * Под кассой праздников — сами чеки (миграция 090). До 04.10.2026 это
           * была единственная плитка экрана без списка: строки кассы приходят
           * сводкой без номера чека. Чеки праздников загружаются по заказам, и
           * список берёт те же сутки, что и плитка.
           */
          drill={{
            kind: "checks",
            title: `${t(TX.drillPartyChecks)} ${dayLabel(report.day)}`,
            hint: t(TX.drillPartyChecksHint),
            slice: {},
            filters: dayWindow(report.day),
          }}
          foot={
            parksSilent > 0
              ? `${t(TX.tillIsBanquetOnly)} · ${num(pos.parks_reporting)} ${t(UI.of)} ${num(
                  pos.parks_total,
                )} ${plural(pos.parks_total, TX.parksReportTill)} · ${t(TX.theOther)} ${num(
                  parksSilent,
                )} ${t(TX.noTillAccess)}`
              : `${t(TX.tillIsBanquetOnly)} · ${t(TX.allLower)} ${num(pos.parks_total)} ${plural(
                  pos.parks_total,
                  TX.parksReportTill,
                )}`
          }
        />
      </div>

      <div className="grid cols-2" style={{ alignItems: "start" }}>
        {/* Левая колонка — график и под ним «Что предстоит»: правая, «На что
            взглянуть», обычно длиннее, и место под графиком пустовало. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
        <div className="card">
          <div className="card-head">
            <h3>{t(TX.leadsAndHandoffs)}</h3>
            <span className="hint">{t(TX.leadsAndHandoffsHint)}</span>
          </div>
          <TrendChart trend={report.trend} />
          <div
            className="sub"
            style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 8 }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <i style={{ width: 16, borderTop: "2px solid var(--accent)" }} />
              {t(TX.leadsCreated)}
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <i style={{ width: 16, borderTop: "2px solid var(--pos)" }} />
              {t(TX.qualifiedLeads)}
            </span>
          </div>
        </div>

        {/*
          ЧИСЛО ВМЕСТО ТАБЛИЦЫ (19.10.2026). Раньше здесь стояла таблица на 12
          строк из ответа, обрезанного сороковой: целиком не увидеть и не
          выгрузить. Теперь под числом обычный список сделок — все строки,
          ссылки в Битрикс, Excel. Окно приходит с сервера: «сегодня» там
          дубайское, а дата браузера бывает UTC.
        */}
        <Tile
          tint="var(--s5)"
          label={t(TX.comingUpInParks)}
          value={num(report.upcoming.events)}
          drill={{
            kind: "deals",
            title: `${t(TX.drillComingUp)} · ${dayLabel(report.upcoming.from_day)} — ${dayLabel(report.upcoming.to_day)}`,
            hint: t(TX.drillComingUpHint),
            slice: { dateBasis: "event", outcome: "not_lost" },
            filters: { ...dayWindow(report.upcoming.from_day), to: report.upcoming.to_day },
          }}
          foot={
            report.upcoming.events === 0 ? (
              t(TX.noEventsSevenDays)
            ) : (
              <>
                {t(TX.comingUpWindow)} ·{" "}
                {report.upcoming.without_paid_booking > 0 ? (
                  <>
                    <DrillNum
                      drill={{
                        kind: "deals",
                        title: `${t(TX.drillComingUp)} · ${t(TX.withoutPaidBooking)}`,
                        hint: t(TX.drillComingUpHint),
                        slice: { dateBasis: "event", outcome: "not_lost", hasPaidBooking: false },
                        filters: { ...dayWindow(report.upcoming.from_day), to: report.upcoming.to_day },
                      }}
                    >
                      {num(report.upcoming.without_paid_booking)}
                    </DrillNum>{" "}
                    {t(TX.withoutPaidBooking)}
                  </>
                ) : (
                  t(TX.allHavePaidBooking)
                )}
              </>
            )
          }
        />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 11, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span style={{ fontFamily: "var(--f-comic)", fontSize: 17, color: "var(--ink)" }}>
              {t(TX.worthALookTitle)}
            </span>
            <span style={{ flex: 1, height: 1, background: "var(--line)" }} />
            {looks.length > 0 && <span className="tag">{num(looks.length)}</span>}
          </div>
          {looks.length === 0 ? (
            <div className="empty">{t(TX.nothingNeedsALook)}</div>
          ) : (
            looks.map((look) => <LookCard key={look.key} look={look} />)
          )}
        </div>
      </div>

    </div>
  );
}
